import React, { useState } from 'react';
import api from '../../api';
import { useToast } from '../UI/ToastContext';

const StyleList = ({ styles, onEditClick, onDeleteStyle }) => {
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState('');
  const { showError } = useToast();

  // Gestion du tri
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Inverser la direction si on clique sur la même colonne
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne, tri ascendant par défaut
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Fonction de tri
  const sortedStyles = [...styles].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];
    
    // Gérer les cas spéciaux (null, undefined)
    if (valA === null || valA === undefined) valA = '';
    if (valB === null || valB === undefined) valB = '';
    
    // Convertir en string pour la comparaison
    if (typeof valA !== 'string') valA = String(valA);
    if (typeof valB !== 'string') valB = String(valB);
    
    const result = valA.localeCompare(valB, undefined, { numeric: true });
    return sortDirection === 'asc' ? result : -result;
  });

  // Filtrage
  const filteredStyles = sortedStyles.filter(style => {
    if (!filter) return true;
    const lowerFilter = filter.toLowerCase();
    return (
      (style.name && style.name.toLowerCase().includes(lowerFilter)) ||
      (style.category && style.category.toLowerCase().includes(lowerFilter)) ||
      (style.description && style.description.toLowerCase().includes(lowerFilter)) ||
      (style.format && style.format.toLowerCase().includes(lowerFilter))
    );
  });

  // Confirmer et gérer la suppression
  const handleDeleteClick = async (styleId) => {
    if (confirmDelete === styleId) {
      const result = await onDeleteStyle(styleId);
      if (!result.success) {
        showError(result.error || 'Erreur lors de la suppression');
      }
      setConfirmDelete(null);
    } else {
      setConfirmDelete(styleId);
    }
  };

  // Afficher une icône pour le tri
  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="panel-content">
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Rechercher des styles..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-primary)' }}>
              <th onClick={() => handleSort('id')} style={headerStyle}>
                ID {getSortIcon('id')}
              </th>
              <th onClick={() => handleSort('name')} style={headerStyle}>
                Nom {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('category')} style={headerStyle}>
                Catégorie {getSortIcon('category')}
              </th>
              <th onClick={() => handleSort('token_cost')} style={headerStyle}>
                Coût {getSortIcon('token_cost')}
              </th>
              <th onClick={() => handleSort('format')} style={headerStyle}>
                Format {getSortIcon('format')}
              </th>
              <th onClick={() => handleSort('is_active')} style={headerStyle}>
                Actif {getSortIcon('is_active')}
              </th>
              <th style={{ ...headerStyle, minWidth: '150px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStyles.length > 0 ? (
              filteredStyles.map(style => (
                <tr key={style.id} style={rowStyle(style.is_active)}>
                  <td style={cellStyle}>{style.id}</td>
                  <td style={cellStyle}>{style.name}</td>
                  <td style={cellStyle}>{style.category}</td>
                  <td style={cellStyle}>{style.token_cost}</td>
                  <td style={cellStyle}>
                    {style.format === 'portrait' ? 'Portrait (1024x1536)' : 'Carré (1024x1024)'}
                  </td>
                  <td style={cellStyle}>
                    <span style={{ 
                      color: style.is_active ? 'var(--color-secondary)' : 'var(--color-error)',
                      fontWeight: 'bold' 
                    }}>
                      {style.is_active ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => onEditClick(style)}
                      >
                        Modifier
                      </button>
                      <button
                        className={`btn ${confirmDelete === style.id ? 'btn-primary' : 'btn-outline'} btn-sm`}
                        onClick={() => handleDeleteClick(style.id)}
                        style={confirmDelete === style.id ? { backgroundColor: 'var(--color-error)' } : {}}
                      >
                        {confirmDelete === style.id ? 'Confirmer' : 'Supprimer'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ ...cellStyle, textAlign: 'center', padding: '20px' }}>
                  {filter ? 'Aucun style ne correspond à votre recherche' : 'Aucun style disponible'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <p>Total: {filteredStyles.length} style(s)</p>
      </div>
    </div>
  );
};

// Styles
const headerStyle = {
  padding: '12px 8px',
  textAlign: 'left',
  backgroundColor: 'rgba(255, 255, 255, 0.05)', 
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  userSelect: 'none'
};

const cellStyle = {
  padding: '12px 8px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
};

const rowStyle = (isActive) => ({
  transition: 'background-color 0.2s',
  opacity: isActive ? 1 : 0.6
});

export default StyleList; 